import { Link, useForm } from '@inertiajs/react';
import { useMemo, useState } from 'react';
import { type FormEvent } from 'react';

import { ErrorSummary, type ErrorSummaryItem } from '@/components/public/forms/ErrorSummary';
import { FormAlert } from '@/components/public/forms/FormAlert';
import Button from '@/components/public/ui/Button';
import RequiredFieldsNote from '@/components/public/forms/RequiredFieldsNote';
import { Callout } from '@/components/public/ui/Callout';
import Icon from '@/components/public/ui/Icon';
import Select from '@/components/public/ui/Select';
import { inputClass, inputErrorClass } from '@/lib/formStyles';
import Seo from '@/components/public/Seo';
import PageHero from '@/components/public/sections/PageHero';
import { parishName, useSite } from '@/hooks/useSite';
import { trackUmamiEvent } from '@/lib/analytics';

type Props = {
  infractionTypes: string[];
};

const INFRACTION_LABELS: Record<string, string> = {
  corruption: 'Corrupção e infrações conexas',
  procurement: 'Contratação pública',
  conflict_of_interest: 'Conflitos de interesses',
  financial_management: 'Gestão financeira ou patrimonial',
  data_protection: 'Proteção de dados pessoais',
  environment_safety: 'Ambiente, saúde pública ou segurança',
  other: 'Outro',
};

const labelClass = 'mb-1.5 block text-sm font-medium text-on-surface';
const helpClass = 'mt-1 block text-xs text-on-surface-light';
const errorClass = 'mt-1 text-xs text-error';
const MIN_SUMMARY_LENGTH = 50;

const FIELD_LABELS: Record<string, string> = {
  infraction_type: 'Tipo de infração',
  summary: 'Descrição da infração',
  facts_when: 'Quando ocorreu',
  facts_who: 'Pessoas envolvidas',
  facts_evidence: 'Elementos de prova adicionais',
  files: 'Anexos',
  email: 'Correio eletrónico para contacto',
  consent: 'Confirmação de veracidade',
};

const FIELD_STEPS: Record<string, 0 | 1 | 2> = {
  infraction_type: 0,
  summary: 1,
  facts_when: 1,
  facts_who: 1,
  facts_evidence: 1,
  files: 2,
  email: 2,
  consent: 2,
};

const FIELD_IDS: Record<string, string> = {
  infraction_type: 'wb-type',
  summary: 'wb-summary',
  facts_when: 'wb-when',
  facts_who: 'wb-who',
  facts_evidence: 'wb-evidence',
  files: 'wb-files',
  email: 'wb-email',
  consent: 'wb-consent',
};

const STEPS = [
  { key: 'classification', title: 'Classificação', description: 'Tipo de infração' },
  { key: 'facts', title: 'Factos', description: 'Descrição e contexto' },
  { key: 'confirmation', title: 'Confirmação', description: 'Anexos, contacto e consentimento' },
] as const;

type StepIndex = 0 | 1 | 2;

export default function WhistleblowingForm({ infractionTypes }: Props) {
  const name = parishName(useSite());
  const [step, setStep] = useState<StepIndex>(0);
  const [attemptedStep, setAttemptedStep] = useState(false);
  const [attemptedSubmitStep, setAttemptedSubmitStep] = useState<StepIndex | null>(null);
  const form = useForm({
    infraction_type: '',
    summary: '',
    facts_when: '',
    facts_who: '',
    facts_evidence: '',
    email: '',
    files: [] as File[],
    consent: false,
  });
  const { data, setData, processing, errors, clearErrors } = form;
  const e = errors as unknown as Record<string, string | undefined>;
  const visibleErrors = { ...e };
  if (data.consent) delete visibleErrors.consent;

  const stepErrors = useMemo(() => {
    const result: Record<StepIndex, string[]> = { 0: [], 1: [], 2: [] };

    if (!data.infraction_type) result[0].push('Selecione o tipo de infração.');
    if (!data.summary.trim()) result[1].push('Descreva a infração.');
    else if (data.summary.trim().length < MIN_SUMMARY_LENGTH) {
      result[1].push(`A descrição deve ter pelo menos ${MIN_SUMMARY_LENGTH} caracteres.`);
    }
    if (!data.consent) result[2].push('Confirme que os factos descritos são verdadeiros.');

    return result;
  }, [data.consent, data.infraction_type, data.summary]);

  const currentStepErrors = stepErrors[step];
  const allStepErrors = [...stepErrors[0], ...stepErrors[1], ...stepErrors[2]];
  const hasAttemptedSubmitOnCurrentStep = attemptedSubmitStep === step;
  const shouldShowStepErrors =
    currentStepErrors.length > 0 && (step === 2 ? hasAttemptedSubmitOnCurrentStep : attemptedStep);
  const summaryLength = data.summary.trim().length;

  const serverErrorItems: ErrorSummaryItem[] = Object.entries(visibleErrors)
    .filter((entry): entry is [string, string] => Boolean(entry[1]))
    .map(([key, message]) => ({
      key,
      label: FIELD_LABELS[key] ?? key,
      message,
      onClick: () => {
        const targetStep = FIELD_STEPS[key];
        if (targetStep !== undefined) setStep(targetStep);
        window.setTimeout(() => document.getElementById(FIELD_IDS[key])?.focus(), 0);
      },
    }));

  const goToStep = (nextStep: StepIndex) => {
    if (nextStep > step) {
      const firstInvalidStep = ([0, 1, 2] as StepIndex[])
        .slice(step, nextStep)
        .find((index) => stepErrors[index].length > 0);
      if (firstInvalidStep !== undefined) {
        setStep(firstInvalidStep);
        setAttemptedStep(true);
        return;
      }
    }

    if (nextStep > step && currentStepErrors.length > 0) {
      setAttemptedStep(true);
      return;
    }
    setStep(nextStep);
    setAttemptedStep(false);
    setAttemptedSubmitStep(null);
  };

  const nextStep = () => {
    if (currentStepErrors.length > 0) {
      setAttemptedStep(true);
      trackUmamiEvent('form_submit_blocked', {
        form_type: 'whistleblowing',
        step: STEPS[step].key,
        error_count: currentStepErrors.length,
      });
      return;
    }
    trackUmamiEvent('whistleblowing_step_continue', {
      from_step: STEPS[step].key,
      to_step: STEPS[(step + 1) as StepIndex].key,
    });
    setStep((step + 1) as StepIndex);
    setAttemptedStep(false);
    setAttemptedSubmitStep(null);
  };

  const previousStep = () => {
    setStep((step - 1) as StepIndex);
    setAttemptedStep(false);
    setAttemptedSubmitStep(null);
  };

  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    setAttemptedSubmitStep(step);
    if (allStepErrors.length > 0) {
      trackUmamiEvent('form_submit_blocked', {
        form_type: 'whistleblowing',
        step: STEPS[step].key,
        error_count: allStepErrors.length,
      });
      return;
    }
    clearErrors();
    form.transform((d) => ({
      ...d,
      consent: d.consent ? 'true' : 'false',
      consented_at: new Date().toISOString(),
    }));
    trackUmamiEvent('whistleblowing_submit', {
      infraction_type: data.infraction_type,
      has_email: data.email.trim().length > 0,
      has_files: data.files.length > 0,
    });
    form.post('/whistleblow-reports', { forceFormData: true });
  };

  const handleFilesChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const files = ev.target.files ? Array.from(ev.target.files) : [];
    setData('files', files);
  };

  return (
    <>
      <Seo
        title="Submeter Denúncia"
        description={`Formulário do Canal de Denúncias da ${name}. Submissão confidencial; pode ser feita sem indicar dados de identificação ou contacto.`}
      />
      <PageHero
        badge="Transparência"
        breadcrumbs={[
          { label: 'Início', href: '/' },
          { label: 'Canal de Denúncias', href: '/transparency/whistleblowing' },
          { label: 'Submeter denúncia' },
        ]}
        title="Submeter Denúncia."
        accentWord="Denúncia."
        subtitle="Comunique uma infração através do canal interno. A sua identidade é protegida nos termos da Lei n.º 93/2021. O preenchimento do email é opcional: pode submeter sem indicar dados de identificação ou contacto."
      />
      <div className="mx-auto max-w-4xl px-4 py-16 md:px-8">
        <div className="mb-8">
          <Callout variant="success" title="A sua submissão é confidencial">
            O acesso aos dados é limitado às pessoas designadas para a receção e tratamento das
            denúncias, nos termos legais. Receberá um código de referência único após a submissão;
            guarde-o para qualquer comunicação futura sobre esta denúncia.
          </Callout>
        </div>

        <form onSubmit={submit} noValidate className="surface-card space-y-6 p-6 md:p-8">
          <div className="grid gap-3 md:grid-cols-3">
            {STEPS.map((item, index) => {
              const isActive = step === index;
              const hasErrors = stepErrors[index as StepIndex].length > 0;
              const isComplete = !hasErrors;

              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => goToStep(index as StepIndex)}
                  className={[
                    'rounded-xl border p-3 text-left transition-colors',
                    isActive
                      ? 'border-primary/35 bg-primary/[0.06]'
                      : 'border-line hover:border-line-strong bg-surface',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <span className="flex items-start justify-between gap-2">
                    <span>
                      <span className="text-on-surface-light block text-[0.65rem] font-bold tracking-widest uppercase">
                        Etapa {index + 1}
                      </span>
                      <span className="font-headline text-on-surface mt-1 block text-base font-semibold">
                        {item.title}
                      </span>
                    </span>
                    {isComplete ? (
                      <Icon
                        name="check_circle"
                        size="sm"
                        className="text-tertiary mt-0.5 shrink-0"
                      />
                    ) : (
                      <span
                        aria-hidden="true"
                        className={[
                          'mt-1 block h-4 w-4 shrink-0 rounded-full border-2',
                          isActive ? 'border-primary' : 'border-on-surface-light/35',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      />
                    )}
                  </span>
                  <span className="text-on-surface-light mt-1 block text-xs">
                    {item.description}
                  </span>
                </button>
              );
            })}
          </div>

          <RequiredFieldsNote />

          <ErrorSummary
            title="Ainda não foi possível submeter a denúncia"
            intro="Reveja os pontos abaixo. Os dados permanecem no formulário até voltar a submeter."
            items={serverErrorItems}
          />

          {shouldShowStepErrors && (
            <FormAlert title="Complete esta etapa para continuar">
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
                {currentStepErrors.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            </FormAlert>
          )}

          {step === 0 && (
            <div>
              <label htmlFor="wb-type" className={labelClass}>
                Tipo de infração
              </label>
              <Select
                id="wb-type"
                value={data.infraction_type}
                onChange={(ev) => setData('infraction_type', ev.target.value)}
                required
                aria-invalid={!!e.infraction_type || (attemptedStep && !data.infraction_type)}
              >
                <option value="">— selecione —</option>
                {infractionTypes.map((t) => (
                  <option key={t} value={t}>
                    {INFRACTION_LABELS[t] ?? t}
                  </option>
                ))}
              </Select>
              {attemptedStep && !data.infraction_type && (
                <p className={errorClass}>Selecione o tipo de infração.</p>
              )}
              {e.infraction_type && <p className={errorClass}>{e.infraction_type}</p>}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label htmlFor="wb-summary" className={labelClass}>
                  Descrição da infração
                </label>
                <textarea
                  id="wb-summary"
                  value={data.summary}
                  onChange={(ev) => setData('summary', ev.target.value)}
                  required
                  rows={6}
                  minLength={MIN_SUMMARY_LENGTH}
                  className={
                    e.summary || (attemptedStep && summaryLength < MIN_SUMMARY_LENGTH)
                      ? inputErrorClass
                      : inputClass
                  }
                  placeholder="Descreva os factos com o maior detalhe possível (mínimo 50 caracteres)…"
                />
                <span className={helpClass}>
                  Mínimo 50 caracteres. Inclua factos concretos e evite especulações ou juízos de
                  valor.
                </span>
                <div className="mt-1 flex items-center justify-between gap-3 text-xs">
                  <span className="text-on-surface-light">{summaryLength}/50 caracteres</span>
                  {attemptedStep && summaryLength < MIN_SUMMARY_LENGTH && (
                    <span className="text-error font-medium">Descrição ainda incompleta.</span>
                  )}
                </div>
                {e.summary && <p className={errorClass}>{e.summary}</p>}
              </div>

              <div>
                <label htmlFor="wb-when" className={labelClass}>
                  Quando ocorreu <span className="text-on-surface-light/60">(opcional)</span>
                </label>
                <input
                  id="wb-when"
                  type="text"
                  value={data.facts_when}
                  onChange={(ev) => setData('facts_when', ev.target.value)}
                  className={e.facts_when ? inputErrorClass : inputClass}
                  placeholder="Ex: março de 2026, ou um período aproximado"
                />
                {e.facts_when && <p className={errorClass}>{e.facts_when}</p>}
              </div>

              <div>
                <label htmlFor="wb-who" className={labelClass}>
                  Pessoas envolvidas <span className="text-on-surface-light/60">(opcional)</span>
                </label>
                <textarea
                  id="wb-who"
                  value={data.facts_who}
                  onChange={(ev) => setData('facts_who', ev.target.value)}
                  rows={3}
                  className={e.facts_who ? inputErrorClass : inputClass}
                  placeholder="Identifique, se possível, quem esteve envolvido (cargo, função, nome)…"
                />
                {e.facts_who && <p className={errorClass}>{e.facts_who}</p>}
              </div>

              <div>
                <label htmlFor="wb-evidence" className={labelClass}>
                  Elementos de prova adicionais{' '}
                  <span className="text-on-surface-light/60">(opcional)</span>
                </label>
                <textarea
                  id="wb-evidence"
                  value={data.facts_evidence}
                  onChange={(ev) => setData('facts_evidence', ev.target.value)}
                  rows={3}
                  className={e.facts_evidence ? inputErrorClass : inputClass}
                  placeholder="Outros elementos que possam ajudar na análise (testemunhas, documentos, entre outros)"
                />
                {e.facts_evidence && <p className={errorClass}>{e.facts_evidence}</p>}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <label htmlFor="wb-files" className={labelClass}>
                  Anexar ficheiros <span className="text-on-surface-light/60">(opcional)</span>
                </label>
                <input
                  id="wb-files"
                  type="file"
                  multiple
                  accept="application/pdf,image/jpeg,image/png,application/zip"
                  onChange={handleFilesChange}
                  className="text-on-surface file:bg-primary/10 file:text-primary hover:file:bg-primary/15 border-line bg-surface w-full rounded-md border px-4 py-2.5 text-sm file:mr-3 file:rounded-md file:border-0 file:px-3 file:py-1 file:text-sm file:font-medium"
                />
                <span className={helpClass}>
                  Até 3 ficheiros, 15 MB no total. PDF, JPG, PNG ou ZIP.
                </span>
                {e.files && <p className={errorClass}>{e.files}</p>}
              </div>

              <div>
                <label htmlFor="wb-email" className={labelClass}>
                  Correio eletrónico para contacto{' '}
                  <span className="text-on-surface-light/60">(opcional)</span>
                </label>
                <input
                  id="wb-email"
                  type="email"
                  value={data.email}
                  onChange={(ev) => setData('email', ev.target.value)}
                  className={e.email ? inputErrorClass : inputClass}
                  placeholder="o.seu.email@exemplo.pt"
                />
                <span className={helpClass}>
                  Deixe em branco para não indicar contacto. Se preencher, será utilizado no âmbito
                  do tratamento da denúncia, nomeadamente para esclarecimentos ou comunicações
                  legalmente previstas.
                </span>
                {e.email && <p className={errorClass}>{e.email}</p>}
              </div>

              <div
                id="wb-consent"
                className={[
                  'rounded-xl border p-4',
                  hasAttemptedSubmitOnCurrentStep && !data.consent
                    ? 'border-error/30 bg-error/5'
                    : 'bg-primary/[0.04] border-primary/15',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <label className="flex items-start gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={data.consent}
                    onChange={(ev) => {
                      setData('consent', ev.target.checked);
                      if (ev.target.checked) {
                        clearErrors('consent');
                        setAttemptedSubmitStep(null);
                      }
                    }}
                    aria-invalid={hasAttemptedSubmitOnCurrentStep && !data.consent}
                    aria-describedby={
                      hasAttemptedSubmitOnCurrentStep && !data.consent
                        ? 'wb-consent-error'
                        : undefined
                    }
                    className="accent-primary mt-0.5 h-4 w-4 shrink-0"
                  />
                  <span className="text-on-surface-light">
                    Confirmo que os factos descritos são, tanto quanto sei, verdadeiros e tomei
                    conhecimento da{' '}
                    <Link href="/privacy" className="text-primary font-medium hover:underline">
                      política de privacidade
                    </Link>
                    . As denúncias são conservadas pelo prazo legal aplicável, incluindo o período
                    mínimo de cinco anos e enquanto se mantenham processos judiciais ou
                    administrativos relacionados.
                  </span>
                </label>
                {hasAttemptedSubmitOnCurrentStep && !data.consent && (
                  <p id="wb-consent-error" className={`${errorClass} ml-7`}>
                    Esta confirmação é obrigatória para submeter a denúncia.
                  </p>
                )}
                {visibleErrors.consent && (
                  <p className={`${errorClass} ml-7`}>{visibleErrors.consent}</p>
                )}
              </div>
            </div>
          )}

          {/* Submit */}
          <div className="border-line mt-2 flex flex-col gap-3 border-t pt-6 sm:flex-row sm:items-center sm:justify-between">
            {step === 0 ? (
              <Link
                href="/transparency/whistleblowing"
                className="text-primary text-sm font-medium hover:underline"
              >
                ← Voltar à página informativa
              </Link>
            ) : (
              <button
                type="button"
                onClick={previousStep}
                className="text-primary text-sm font-medium hover:underline"
              >
                ← Etapa anterior
              </button>
            )}

            {step < 2 ? (
              <Button type="button" variant="primary" iconAfter="arrow_forward" onClick={nextStep}>
                Continuar
              </Button>
            ) : (
              <Button
                type="submit"
                variant="primary"
                iconAfter="arrow_forward"
                disabled={processing}
                loading={processing}
              >
                {processing ? 'A submeter…' : 'Submeter denúncia'}
              </Button>
            )}
          </div>
        </form>
      </div>
    </>
  );
}
